import { useCallback, useEffect, useRef } from 'react';
import {
  Channel,
  useAppBridge,
  useAppContext,
  useMenuStore,
  useRouterHistory,
} from 'scbk-services';
import { useSession } from 'scbk-shared-module/modules/Session';
import { AdobeClient } from '../client/adobeClient';
import { getAnalyticsPlatform } from '../config/adobeConfig';
import { AdobeDataLayerService } from '../service';
import { PageInfo, PushParameter } from '../types';
import { useAnalyticsLoader } from './useAnalyticsLoader';

export const useAdobeDataLayer = () => {
  const screenName = useRef('');

  const { currentRoute } = useRouterHistory.getState();
  const { getContext: appContext } = useAppContext();
  const { currentMenu } = useMenuStore();
  const { isLogin, getUserInfo, getLoginInfo } = useSession();
  const { enabled, ready } = useAnalyticsLoader();

  const { doNGetDeviceStorage } = useAppBridge();

  const platform = getAnalyticsPlatform(appContext().channel);

  const getLoginType = useCallback(async () => {
    if (getUserInfo().connectType) {
      return getUserInfo().connectType;
    }

    if (appContext().channel == Channel.MB) {
      return await doNGetDeviceStorage('CMM', 'LAST_LOGIN_TYPE');
    } else {
      return getUserInfo().lastLoginType;
    }
  }, []);

  const getMemberTier = useCallback(async () => {
    const loginInfo = await getLoginInfo(['yoPBank']);
    const memberTier = loginInfo?.yoPBank;
    return ['1', '2', '3'].includes(memberTier ?? '') ? memberTier : '4';
  }, []);

  /**
   * pageInfo 설정 - 모든 수집정보에 공통적으로 사용되는 설정
   */
  const basePageInfoPageInfo = useCallback((): PageInfo => {
    return {
      event: '',
      platform,
      screen_id: currentRoute?.id ?? '',
      screen_name: screenName.current,
      menu_id: currentMenu?.id ?? '',
      menu_name: currentMenu?.name ?? '',
      login_status: isLogin() ? 'y' : 'n',
    };
  }, []);

  /**
   * user 설정
   */
  const baseUserInfo = useCallback(async () => {
    const memberTier = await getMemberTier();

    return {
      user: {
        member_type: '개인',
        login_type: await getLoginType(),
        member_tier: memberTier,
      },
    };
  }, [getMemberTier, getLoginType]);

  /**
   * Adobe 공통 수집 함수
   */
  const push = useCallback(
    async ({ event, screen_name, data = {}, includeUserInfo = false }: PushParameter) => {
      if (!enabled) return;

      if (screen_name) {
        screenName.current = screen_name;
      }

      const payload: PageInfo = {
        ...basePageInfoPageInfo(),
        ...data,
        ...(includeUserInfo ? await baseUserInfo() : {}),
        event,
      };

      AdobeClient.push(payload);
    },
    [enabled],
  );

  /**
   * JS가 로드되기 전에 push를 방지하기 위한 로드전에는 queue 에 쌓아두고 로드가 완료된 후 queue에 적재된 데이터 push
   */
  useEffect(() => {
    if (!enabled || !ready) return;

    AdobeClient.setReady(true);
    AdobeClient.flush();
  }, [enabled, ready]);

  /**
   * Dialog와 같은 일반함수에서 Tracking 이 필요할 경우 서비스 핸들러 별도로 제공
   */
  useEffect(() => {
    AdobeDataLayerService.setHandler({
      push,
    });
  }, [push]);

  return {
    push,
  };
};
